// ──────────────────────────────────────────────
// Questionnaire Mapping — Role → Party Type → Questionnaire
// ──────────────────────────────────────────────
//
// This module resolves which of the 6 questionnaire pools to render
// for a collaboration, based on the two stored CollaborationRole values:
//   - requesterRole   (what role the requester picked for themselves)
//   - expectedPartnerRole (what role the requester expects the partner to play)
//
// Flow:
//   CollaborationRole → PartyType → (PartyTypeA, PartyTypeB) → QuestionnaireId
//
// Priority:  JV > R&D > lookup table
// ──────────────────────────────────────────────

// ─── Party Types ─────────────────────────────
// These are the abstract "company archetypes" used in the client's
// permutation tables. Each CollaborationRole maps to exactly one.

export type PartyType =
  | "MANUFACTURER"
  | "DISTRIBUTOR"
  | "TECH_LICENSOR"
  | "TECH_LICENSEE"
  | "SERVICE_PROVIDER"
  | "RND"
  | "JV";

// ─── Questionnaire IDs ──────────────────────
// Must match the "id" field inside each questionnaire JSON file.

export const QUESTIONNAIRE_IDS = {
  MANUFACTURER: "manufacturer_unified_collaboration_v1",
  DISTRIBUTOR: "distribution_market_access_v1",
  TECHNOLOGY_LICENSING: "technology_licensing_single_stage_v1",
  OUTSOURCING_API: "outsourcing_managed_services_api_integration_v1",
  RND: "rnd_co_development_unified_v1",
  JV: "joint_venture_universal_v1",
} as const;

export type QuestionnaireId = (typeof QUESTIONNAIRE_IDS)[keyof typeof QUESTIONNAIRE_IDS];

// ─── Role → Party Type Mapping ──────────────

const ROLE_TO_PARTY_TYPE: Record<string, PartyType> = {
  // Manufacturer roles
  BRAND_OWNER_OEM_PRODUCT_OWNER: "MANUFACTURER",
  CONTRACT_MANUFACTURER_SUPPLIER: "MANUFACTURER",

  // Distributor roles
  DISTRIBUTOR_CHANNEL_PARTNER: "DISTRIBUTOR",
  LEAD_DISTRIBUTOR: "DISTRIBUTOR",
  REGIONAL_DISTRIBUTOR: "DISTRIBUTOR",

  // Technology roles
  TECHNOLOGY_IP_PROVIDER: "TECH_LICENSOR",
  TECHNOLOGY_USER_LICENSEE: "TECH_LICENSEE",

  // R&D roles
  RND_LEAD_INNOVATION_OWNER: "RND",
  RND_PARTNER_CONTRIBUTOR: "RND",

  // Service Provider roles
  CAPABILITY_SERVICES_PROVIDER: "SERVICE_PROVIDER",
  SOLUTION_SEEKER: "SERVICE_PROVIDER",

  // JV
  STRATEGIC_JV_PARTNER: "JV",
};

/**
 * Maps a CollaborationRole enum value to its abstract PartyType.
 *
 * @throws if the role is not recognized.
 */
export function getPartyType(collaborationRole: string): PartyType {
  const partyType = ROLE_TO_PARTY_TYPE[collaborationRole];
  if (!partyType) {
    throw new Error(`Unknown CollaborationRole: "${collaborationRole}"`);
  }
  return partyType;
}

// ─── Party Type Pair → Questionnaire Lookup ──
//
// Priority rules (checked first):
//   1. If either party is JV → JV questionnaire
//   2. If either party is R&D → R&D questionnaire
//   3. Otherwise → lookup table below
//
// The lookup table uses a sorted key "TYPE_A|TYPE_B" (alphabetical)
// so that (MANUFACTURER, DISTRIBUTOR) and (DISTRIBUTOR, MANUFACTURER)
// both resolve to the same entry.
//
// Core party types (excluding RND & JV which are handled by priority):
// MANUFACTURER, DISTRIBUTOR, TECH_LICENSOR, TECH_LICENSEE, SERVICE_PROVIDER

/**
 * Sorted-key lookup for the non-priority party type pairs.
 *
 * Derived from the client's permutation tables:
 *
 * | Party A            | Party B            | Questionnaire       |
 * |--------------------|--------------------|---------------------|
 * | Manufacturer       | Manufacturer       | Manufacturer        |
 * | Manufacturer       | Distributor        | Distributor         |
 * | Manufacturer       | Tech Licensor      | Technology Licensing |
 * | Manufacturer       | Tech Licensee      | Technology Licensing |
 * | Manufacturer       | Service Provider   | Outsourcing/API     |
 * | Distributor        | Distributor        | Distributor         |
 * | Distributor        | Tech Licensor      | R&D                 |
 * | Distributor        | Tech Licensee      | R&D                 |
 * | Distributor        | Service Provider   | Outsourcing/API     |
 * | Tech Licensor      | Tech Licensor      | Technology Licensing |
 * | Tech Licensor      | Tech Licensee      | Technology Licensing |
 * | Tech Licensor      | Service Provider   | Outsourcing/API     |
 * | Tech Licensee      | Tech Licensee      | Technology Licensing |
 * | Tech Licensee      | Service Provider   | Outsourcing/API     |
 * | Service Provider   | Service Provider   | Outsourcing/API     |
 */
const PAIR_TO_QUESTIONNAIRE: Record<string, QuestionnaireId> = {
  // Manufacturer pairs
  "MANUFACTURER|MANUFACTURER": QUESTIONNAIRE_IDS.MANUFACTURER,
  "DISTRIBUTOR|MANUFACTURER": QUESTIONNAIRE_IDS.DISTRIBUTOR,
  "MANUFACTURER|TECH_LICENSOR": QUESTIONNAIRE_IDS.TECHNOLOGY_LICENSING,
  "MANUFACTURER|TECH_LICENSEE": QUESTIONNAIRE_IDS.TECHNOLOGY_LICENSING,
  "MANUFACTURER|SERVICE_PROVIDER": QUESTIONNAIRE_IDS.OUTSOURCING_API,

  // Distributor pairs
  "DISTRIBUTOR|DISTRIBUTOR": QUESTIONNAIRE_IDS.DISTRIBUTOR,
  "DISTRIBUTOR|TECH_LICENSOR": QUESTIONNAIRE_IDS.RND,
  "DISTRIBUTOR|TECH_LICENSEE": QUESTIONNAIRE_IDS.RND,
  "DISTRIBUTOR|SERVICE_PROVIDER": QUESTIONNAIRE_IDS.OUTSOURCING_API,

  // Tech pairs
  "TECH_LICENSOR|TECH_LICENSOR": QUESTIONNAIRE_IDS.TECHNOLOGY_LICENSING,
  "TECH_LICENSOR|TECH_LICENSEE": QUESTIONNAIRE_IDS.TECHNOLOGY_LICENSING,
  "TECH_LICENSOR|SERVICE_PROVIDER": QUESTIONNAIRE_IDS.OUTSOURCING_API,
  "TECH_LICENSEE|TECH_LICENSEE": QUESTIONNAIRE_IDS.TECHNOLOGY_LICENSING,
  "SERVICE_PROVIDER|TECH_LICENSEE": QUESTIONNAIRE_IDS.OUTSOURCING_API,

  // Service Provider pairs
  "SERVICE_PROVIDER|SERVICE_PROVIDER": QUESTIONNAIRE_IDS.OUTSOURCING_API,
};

/**
 * Creates a deterministic sorted lookup key from two party types.
 * Ensures (A, B) and (B, A) produce the same key.
 */
function makePairKey(a: string, b: string): string {
  return [a, b].sort().join("|");
}

/**
 * Resolves which questionnaire to show for a collaboration, given
 * the two CollaborationRole enum values stored on the match.
 *
 * @param requesterRole - The CollaborationRole the requester picked for themselves.
 * @param expectedPartnerRole - The CollaborationRole the requester expects the partner to play.
 * @returns The questionnaire ID string (matches the `id` field in the JSON files).
 *
 * @throws if either role is unrecognized or the party type pair has no mapping.
 *
 * @example
 * ```ts
 * const qId = getQuestionnaireId("BRAND_OWNER_OEM_PRODUCT_OWNER", "LEAD_DISTRIBUTOR");
 * // → "distribution_market_access_v1"
 *
 * const qId2 = getQuestionnaireId("RND_LEAD_INNOVATION_OWNER", "BRAND_OWNER_OEM_PRODUCT_OWNER");
 * // → "rnd_co_development_unified_v1"  (R&D priority override)
 *
 * const qId3 = getQuestionnaireId("STRATEGIC_JV_PARTNER", "TECHNOLOGY_IP_PROVIDER");
 * // → "joint_venture_universal_v1"  (JV priority override)
 * ```
 */
export function getQuestionnaireId(
  requesterRole: string,
  expectedPartnerRole: string
): QuestionnaireId {
  const partyA = getPartyType(requesterRole);
  const partyB = getPartyType(expectedPartnerRole);

  // Priority 1: JV overrides everything
  if (partyA === "JV" || partyB === "JV") {
    return QUESTIONNAIRE_IDS.JV;
  }

  // Priority 2: R&D overrides non-JV
  if (partyA === "RND" || partyB === "RND") {
    return QUESTIONNAIRE_IDS.RND;
  }

  // Priority 3: Lookup table
  const key = makePairKey(partyA, partyB);
  const questionnaireId = PAIR_TO_QUESTIONNAIRE[key];

  if (!questionnaireId) {
    throw new Error(
      `No questionnaire mapping for party type pair: (${partyA}, ${partyB}) [key: ${key}]`
    );
  }

  return questionnaireId;
}

// ─── Questionnaire Metadata ──────────────────

export const QUESTIONNAIRE_LABELS: Record<QuestionnaireId, string> = {
  [QUESTIONNAIRE_IDS.MANUFACTURER]: "Manufacturer Collaboration Assessment",
  [QUESTIONNAIRE_IDS.DISTRIBUTOR]: "Distribution Partnership Assessment",
  [QUESTIONNAIRE_IDS.TECHNOLOGY_LICENSING]: "Technology Licensing Assessment",
  [QUESTIONNAIRE_IDS.OUTSOURCING_API]: "Outsourcing / Managed Services & API Integration Assessment",
  [QUESTIONNAIRE_IDS.RND]: "R&D / Co-Development Assessment",
  [QUESTIONNAIRE_IDS.JV]: "Joint Venture Assessment",
};

/**
 * Returns human-readable label for a questionnaire ID.
 */
export function getQuestionnaireLabel(questionnaireId: QuestionnaireId): string {
  return QUESTIONNAIRE_LABELS[questionnaireId] ?? questionnaireId;
}

// ─── Reverse Mapping: Role → CollaborationIntent ──
//
// Each CollaborationRole belongs to one CollaborationIntent "family".
// This is used when you need to derive the collaboration intent
// from a stored role value (e.g., for display purposes).

const ROLE_TO_COLLABORATION_INTENT: Record<string, string> = {
  BRAND_OWNER_OEM_PRODUCT_OWNER: "MANUFACTURING",
  CONTRACT_MANUFACTURER_SUPPLIER: "MANUFACTURING",
  DISTRIBUTOR_CHANNEL_PARTNER: "DISTRIBUTION_MARKET_ACCESS",
  LEAD_DISTRIBUTOR: "DISTRIBUTION_MARKET_ACCESS",
  REGIONAL_DISTRIBUTOR: "DISTRIBUTION_MARKET_ACCESS",
  TECHNOLOGY_IP_PROVIDER: "TECHNOLOGY_LICENSING",
  TECHNOLOGY_USER_LICENSEE: "TECHNOLOGY_LICENSING",
  RND_LEAD_INNOVATION_OWNER: "RD_CO_DEVELOPMENT",
  RND_PARTNER_CONTRIBUTOR: "RD_CO_DEVELOPMENT",
  CAPABILITY_SERVICES_PROVIDER: "OUTSOURCING_MANAGED_SERVICES_API_SOFTWARE_INTEGRATION",
  SOLUTION_SEEKER: "OUTSOURCING_MANAGED_SERVICES_API_SOFTWARE_INTEGRATION",
  STRATEGIC_JV_PARTNER: "STRATEGIC_JOINT_VENTURE",
};

/**
 * Given a CollaborationRole, returns the CollaborationIntent it belongs to.
 *
 * @throws if the role is not recognized.
 */
export function getCollaborationIntentFromRole(collaborationRole: string): string {
  const intent = ROLE_TO_COLLABORATION_INTENT[collaborationRole];
  if (!intent) {
    throw new Error(`Unknown CollaborationRole for intent mapping: "${collaborationRole}"`);
  }
  return intent;
}
